import React, { useRef, useEffect, useState } from "react";
import { couponTypes } from "../../constants";
import PropTypes from "prop-types";

const DropDown = ({
  index = null,
  value,
  setValue,
  data = [],
  placeHolder = "Select",
  customizeDropDown = {
    maxWidth: "md:max-w-38.5",
    height: "h-full",
    contentHeight: "h-10"
  },
  showOption,
  setShowOption,
  isEdit,
  currentDropDownIndex,
  setCurrentDropDownIndex,
  textwidth = "w-50",
  buttonText,
  buttonIcon,
  buttonOnClick,
  blackArrow = false,
  onScrollToEnd,
  isLoading,
  isSearchable
}) => {
  const dropdownContentRef = useRef(null);
  const [isFetching, setIsFetching] = useState(false); // To prevent multiple API calls
  const [searchQuery, setSearchQuery] = useState(""); // State for search query

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownContentRef.current &&
        !dropdownContentRef.current.contains(event.target)
      ) {
        setShowOption((prev) => ({
          ...prev,
          [index]: false
        }));
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [setShowOption, index]);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const filteredData = data.filter(
    (item) => item?.name.toLowerCase().includes(searchQuery.toLowerCase()) // Filter items by name based on search query
  );

  const handleScroll = () => {
    if (!dropdownContentRef.current || isFetching || isLoading) return; // Prevent if already fetching or loading

    const { scrollTop, scrollHeight, clientHeight } =
      dropdownContentRef.current;

    if (scrollTop + clientHeight >= scrollHeight - 10) {
      // Trigger callback when near the bottom
      if (onScrollToEnd) {
        setIsFetching(true); // Set fetching state to true
        onScrollToEnd().finally(() => setIsFetching(false)); // Reset fetching state when API call finishes
      }
    }
  };

  const handleChange = (item) => {
    setValue({
      name: item?.name === "None" ? placeHolder : item?.name,
      value: item?._id || item?.value
    });
    setShowOption((prev) => ({
      ...prev,
      [index]: false
    }));
    if (searchQuery) setSearchQuery("");
  };

  const handleClick = (index) => {
    if (index != null) {
      setCurrentDropDownIndex(index);
    }
    setShowOption((prev) => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const getNameOrPlaceHolder = (value) => {
    const name = value?.name;
    const couponName = value?.value;
    if (
      couponName === couponTypes.GLOBAL.NAME ||
      couponName === couponTypes.UNIQUE.NAME
    ) {
      return name;
    }
    if (name) {
      return name;
    }
    return placeHolder;
  };

  const options = (data) => {
    const handleScrollableDivScroll = (event) => {
      if (onScrollToEnd) {
        handleScroll(event); // Pass the event to your existing scroll handler
      }
    };

    return (
      <ul
        key={data?.name}
        ref={dropdownContentRef}
        className={`absolute z-10 mt-12.5 w-full flex-col rounded border border-grey-D9D9D9 bg-white`}
      >
        {/* Fixed Button */}
        {buttonText && (
          <li className="z-20 border-b border-grey-D9D9D9 bg-white">
            <button
              className="flex w-full items-center justify-between p-2 pr-3 hover:bg-purple-6C36B0 hover:text-white"
              onClick={buttonOnClick}
            >
              <span>{buttonText}</span>
              {buttonIcon && (
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11.8337 6.83268H6.83366V11.8327H5.16699V6.83268H0.166992V5.16602H5.16699V0.166016H6.83366V5.16602H11.8337V6.83268Z"
                    className="icon-path"
                  />
                </svg>
              )}
            </button>
          </li>
        )}

        {/* Fixed Search Input */}
        {isSearchable && (
          <li className="z-20 border-b border-grey-D9D9D9 bg-white">
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search"
              className="flex w-full p-2.5 font-MontserratRegular text-ft15 outline-none"
            />
          </li>
        )}

        {/* Scrollable List with Pagination */}
        <div
          className="max-h-80 overflow-y-auto"
          onScroll={handleScrollableDivScroll}
        >
          {filteredData.map((item) => (
            <li
              className="flex flex-col gap-2 overflow-hidden whitespace-nowrap rounded bg-center p-2 hover:bg-purple-6C36B0 hover:text-white"
              key={item?._id}
              onClick={() => handleChange(item)}
            >
              {item?.name}
            </li>
          ))}
          {isLoading && (
            <li className="flex items-center justify-center p-2 text-grey-500">
              Loading...
            </li>
          )}
        </div>
      </ul>
    );
  };

  const hideAllText = () => {
    if (value?.name || value?.value || placeHolder === "Choose Category") {
      return true;
    }
    return false;
  };

  return (
    <div
      ref={dropdownContentRef}
      className={` ${isEdit && "pointer-events-none bg-grey-25"} ${
        (placeHolder === "Select Tier" || placeHolder === "Select Reward") &&
        "bg-white"
      } relative flex size-full cursor-pointer rounded border border-grey-D9D9D9 ${
        customizeDropDown?.height + " " + customizeDropDown.maxWidth
      } `}
    >
      <div
        onClick={() => handleClick(index)}
        className={`flex ${customizeDropDown.contentHeight} w-full items-center justify-between px-3`}
      >
        <div className={`${textwidth}`}>
          <p className="truncate whitespace-nowrap font-MontserratMedium text-ft15-22.5-0.3 text-black-000000">
            {getNameOrPlaceHolder(value)}
          </p>
        </div>
        <span
          className={`${hideAllText() && "hidden"} ml-auto pr-2 font-MontserratMedium text-ft15-26.25 text-black-000000 `}
        >
          All
        </span>
        <img
          className={`${
            index != null
              ? showOption[index] &&
                index === currentDropDownIndex &&
                "-rotate-180"
              : showOption[index] && "-rotate-180"
          } text-purple-6C36B0 duration-100`}
          src={
            blackArrow
              ? "/images/arrow-down.svg"
              : "/images/dropdown-arrow-purple.svg"
          }
        />
      </div>
      {index != null
        ? showOption[index] &&
          index === currentDropDownIndex &&
          options(filteredData)
        : showOption[index] && options(filteredData)}
    </div>
  );
};

DropDown.propTypes = {
  index: PropTypes.number,
  value: PropTypes.any.isRequired,
  setValue: PropTypes.func.isRequired,
  data: PropTypes.array,
  placeHolder: PropTypes.string,
  customizeDropDown: PropTypes.shape({
    maxWidth: PropTypes.string,
    height: PropTypes.string,
    contentHeight: PropTypes.string
  }),
  showOption: PropTypes.bool.isRequired,
  setShowOption: PropTypes.func.isRequired,
  isEdit: PropTypes.bool,
  currentDropDownIndex: PropTypes.number,
  setCurrentDropDownIndex: PropTypes.func.isRequired,
  textwidth: PropTypes.string,
  buttonText: PropTypes.string,
  buttonIcon: PropTypes.string,
  buttonOnClick: PropTypes.func,
  blackArrow: PropTypes.bool,
  onScrollToEnd: PropTypes.func,
  isLoading: PropTypes.bool,
  isSearchable: PropTypes.bool
};

export default DropDown;
